import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { getLiveSessionReports, LiveSessionReportsResult } from '../services/liveAccessApi';

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (value?: number) => {
  if (typeof value !== 'number' || value <= 0) return 'N/A';

  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const seconds = value % 60;

  return [hours, minutes, seconds]
    .map((part, index) => (index === 0 ? String(part) : String(part).padStart(2, '0')))
    .join(':');
};

const InfoCard: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className='rounded-xl border border-gray-200 bg-white p-4 shadow-sm'>
    <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>{label}</p>
    <div className='mt-2 text-sm font-medium text-gray-900 break-words'>{value}</div>
  </div>
);

const Badge: React.FC<{ label: string; tone?: 'emerald' | 'rose' | 'amber' | 'slate' | 'blue' }> = ({
  label,
  tone = 'slate',
}) => {
  const className = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  }[tone];

  return <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${className}`}>{label}</span>;
};

const LiveSessionDetail: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<LiveSessionReportsResult | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!sessionId) return;

    setLoading(true);
    try {
      const response = await getLiveSessionReports(sessionId);
      setData(response);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load live session details');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const session = data?.session;
  const host = data?.host;
  const recentReports = useMemo(() => data?.reports.slice(0, 3) ?? [], [data?.reports]);

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='flex items-center justify-center py-32'>
          <div className='h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#FF9C15]' />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='max-w-4xl mx-auto px-4 py-16 text-center'>
          <p className='text-gray-500'>Live session not found.</p>
          <button
            onClick={() => navigate('/live-access/streams')}
            className='mt-4 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800'
          >
            Back to Live Streams
          </button>
        </div>
      </div>
    );
  }

  const displayTitle = session.Title || 'Untitled live';
  const hostDisplayName = host?.Name || host?.UserName || session.HostUserID;
  const canPlayRecording = Boolean(session.recordingUrl);
  const isLiveNow = session.LifecycleStatus === 'LIVE' || session.LifecycleStatus === 'STARTING';

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <button
          onClick={() => navigate('/live-access/streams')}
          className='mb-6 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900'
        >
          <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
          Back to Live Streams
        </button>

        <div className='grid gap-6 lg:grid-cols-[1.5fr,1fr]'>
          <div className='overflow-hidden rounded-2xl border border-gray-200 bg-black shadow-sm'>
            {canPlayRecording ? (
              <video
                src={session.recordingUrl || undefined}
                poster={session.CoverUrl || undefined}
                controls
                className='h-[420px] w-full bg-black object-contain'
              />
            ) : session.CoverUrl ? (
              <img src={session.CoverUrl} alt={displayTitle} className='h-[420px] w-full object-cover' />
            ) : (
              <div className='flex h-[420px] items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black text-center text-white'>
                <div className='max-w-md px-6'>
                  <p className='text-xl font-semibold'>No recording available yet</p>
                  <p className='mt-3 text-sm text-gray-300'>
                    {isLiveNow
                      ? 'This moderation client can inspect the live session and its reports, but embedded LiveKit playback is not wired here yet.'
                      : 'This livestream has no published recording key on the current API response.'}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className='space-y-4'>
            <div className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
              <div className='flex flex-wrap items-start justify-between gap-3'>
                <div>
                  <h1 className='text-2xl font-bold text-gray-900'>{displayTitle}</h1>
                  <p className='mt-1 text-sm text-gray-500'>Host: {hostDisplayName}</p>
                  <p className='mt-1 text-xs font-mono text-gray-400'>{session.SessionID}</p>
                </div>
                <div className='flex flex-wrap gap-2'>
                  <Badge
                    label={session.LifecycleStatus}
                    tone={session.LifecycleStatus === 'LIVE' ? 'emerald' : session.LifecycleStatus === 'STARTING' ? 'amber' : 'slate'}
                  />
                  <Badge label={session.PublishStatus || 'N/A'} tone={session.PublishStatus === 'PUBLISHED' ? 'blue' : 'slate'} />
                </div>
              </div>

              <div className='mt-5 grid grid-cols-2 gap-3'>
                <InfoCard label='Reports' value={data?.reportCount ?? 0} />
                <InfoCard label='Reporters' value={data?.reporterCount ?? 0} />
                <InfoCard label='Started' value={formatDate(session.StartedAt)} />
                <InfoCard label='Ended' value={formatDate(session.EndedAt)} />
                <InfoCard label='Recording' value={session.RecordingStatus || 'N/A'} />
                <InfoCard label='Duration' value={formatDuration(session.RecordingDurationSeconds)} />
              </div>

              <div className='mt-5 flex flex-wrap gap-3'>
                <button
                  onClick={() => navigate(`/live-access/reports/${encodeURIComponent(session.SessionID)}`)}
                  className='rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95'
                >
                  View Full Reports
                </button>
                <button
                  onClick={() => navigate(`/live-access/reports?hostUserId=${encodeURIComponent(session.HostUserID)}`)}
                  className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50'
                >
                  Host Reports
                </button>
              </div>
            </div>

            <div className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
              <h2 className='text-base font-semibold text-gray-900'>Session Details</h2>
              <div className='mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2'>
                <InfoCard label='Host User ID' value={session.HostUserID} />
                <InfoCard label='Room Name' value={session.RoomName || 'N/A'} />
                <InfoCard label='Category' value={session.Category || 'N/A'} />
                <InfoCard label='Content Rating' value={session.ContentRating || 'N/A'} />
                <InfoCard label='Last Reported' value={formatDate(data?.lastReportedAt)} />
                <InfoCard label='Published Video ID' value={session.RecordingPublishedVideoID || 'N/A'} />
              </div>
            </div>

            <div className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
              <h2 className='text-base font-semibold text-gray-900'>Chat Policy</h2>
              <div className='mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3'>
                <InfoCard label='Chat Enabled' value={session.Policy?.chatEnabled ? 'Yes' : 'No'} />
                <InfoCard label='Followers Only' value={session.Policy?.followersOnlyChat ? 'Yes' : 'No'} />
                <InfoCard label='Slow Mode' value={`${session.Policy?.slowModeSeconds ?? 0}s`} />
              </div>
            </div>
          </div>
        </div>

        <div className='mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
          <div className='flex items-center justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>Recent Reports</h2>
              <p className='text-sm text-gray-500'>Latest moderation signals raised against this livestream.</p>
            </div>
            <button
              onClick={() => navigate(`/live-access/reports/${encodeURIComponent(session.SessionID)}`)}
              className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
            >
              Open Report Drilldown
            </button>
          </div>

          {recentReports.length === 0 ? (
            <div className='mt-6 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-5 py-8 text-center text-sm text-gray-500'>
              No reports have been submitted for this live session.
            </div>
          ) : (
            <div className='mt-6 grid gap-4 lg:grid-cols-3'>
              {recentReports.map((report) => (
                <div key={report.ReportID} className='rounded-xl border border-gray-200 bg-gray-50 p-4'>
                  <div className='flex items-start justify-between gap-3'>
                    <div>
                      <p className='text-sm font-semibold text-gray-900'>{report.ReasonLabel || report.ReasonCode || 'Report'}</p>
                      <p className='mt-1 text-xs text-gray-500'>
                        Reporter: {report.ReporterUserName ? `@${report.ReporterUserName}` : report.ReporterUserID || 'Unknown'}
                      </p>
                    </div>
                    {report.BlockHost && <Badge label='Block Host' tone='rose' />}
                  </div>
                  <p className='mt-3 text-sm text-gray-700'>{report.Details || 'No extra details provided.'}</p>
                  <p className='mt-3 text-xs text-gray-400'>{formatDate(report.createdAt)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveSessionDetail;
